import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { ParteAr } from '../../types/ApparecchiArTypes';

/**
 * Crea o corregge una parte dell'apparecchio.
 *
 * ⚠️ **Le parti non si possono dare per finite quando arrivano dal telefono.** Un
 * «indicatore LED» è una voce sola in catalogo ma sull'apparecchio sono tre spie, e
 * ognuna vuole dire una cosa diversa: senza un modo di scomporle, il bot può solo dire
 * «le spie sono lì» quando la domanda era quale delle tre sta lampeggiando.
 *
 * ⚠️ **La SIGLA è la serigrafia del costruttore, non un'abbreviazione inventata.** È
 * quella che il lettore di scritte dell'app confronta per proporre gli abbinamenti, ed
 * è quella che l'installatore legge sull'apparecchio: inventarne una la rende inutile
 * per entrambi. Se una parte non ha serigrafia, si lascia vuota — è un'informazione,
 * non una lacuna da riempire.
 */

export interface Props {
  aperta: boolean;
  /** null = se ne crea una nuova. */
  parte: ParteAr | null;
  /** Le altre parti: servono a impedire due id uguali. */
  esistenti: ParteAr[];
  onChiudi: () => void;
  onSalva: (p: ParteAr) => void;
}

export default function SchedaParte({ aperta, parte, esistenti, onChiudi, onSalva }: Props) {
  const [nome, setNome] = useState('');
  const [sigla, setSigla] = useState('');
  const [spiega, setSpiega] = useState('');

  useEffect(() => {
    if (!aperta) return;
    setNome(parte?.nome ?? '');
    setSigla(parte?.sigla ?? '');
    setSpiega(parte?.spiega ?? '');
  }, [aperta, parte]);

  const id = parte?.id ?? idDa(nome);
  const collide = !parte && id.length > 0 && esistenti.some((p) => p.id === id);
  const valida = nome.trim().length > 1 && !collide;

  return (
    <Dialog open={aperta} onClose={onChiudi} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {parte ? 'Correggi la parte' : 'Aggiungi una parte'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField
            label="Come si chiama"
            placeholder="Spia di allarme"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            helperText="In italiano, come lo direbbe un tecnico. È quello che legge l'installatore."
            autoFocus
            fullWidth
          />
          <TextField
            label="Sigla serigrafata sull'apparecchio"
            placeholder="ALARM"
            value={sigla}
            onChange={(e) => setSigla(e.target.value)}
            helperText="Copiala com'è stampata. Lasciala vuota se non c'è: non inventarla."
            fullWidth
            InputProps={{
              // ⚠️ Monospaziato: è un codice, si confronta carattere per carattere.
              sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
            }}
          />
          <TextField
            label="Cosa fa"
            placeholder="Lampeggia in rosso quando l'inverter è in guasto e non produce."
            value={spiega}
            onChange={(e) => setSpiega(e.target.value)}
            helperText="Una o due frasi. Compaiono sotto l'indicazione, in cantiere."
            multiline
            minRows={2}
            fullWidth
          />
          {collide && (
            <Alert severity="warning">
              Esiste già una parte con questo nome. Cambialo, o correggi quella che c'è.
            </Alert>
          )}
          {!parte && nome.trim().length > 1 && !collide && (
            <Typography variant="caption" color="text.secondary">
              identificativo:{' '}
              <code style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{id}</code>
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onChiudi}>Annulla</Button>
        <Button
          variant="contained"
          disabled={!valida}
          onClick={() =>
            onSalva({ id, nome: nome.trim(), sigla: sigla.trim(), spiega: spiega.trim() })
          }
        >
          {parte ? 'Salva' : 'Aggiungi'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * L'identificativo si ricava dal nome.
 *
 * ⚠️ Non cambia mai più dopo la creazione: i segni sulle fotografie e le domande del bot
 * lo nominano, e rinominarlo scollegherebbe in silenzio i cerchi già posati dalla parte
 * che indicano. Per questo la correzione di una parte lascia l'id com'è.
 */
function idDa(nome: string): string {
  return nome
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}
